import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/profile-components/Navbar';
import Topbar from '../components/profile-components/Topbar';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Papa from 'papaparse';
import './SalesPages.css';
import { authFetch, authJsonFetch } from '../utils/authFetch';

function Opportunities() {
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    type: '',
    coverageType: '',
    carrier: '',
    nextStep: '',
  });
  const [exportOpen, setExportOpen] = useState(false);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sortField, setSortField] = useState('dueDate');
  const [sortDirection, setSortDirection] = useState('asc');

  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkNextStep, setBulkNextStep] = useState('');
  const [bulkDueDate, setBulkDueDate] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const loadOpportunities = async () => {
      try {
        setLoading(true);

        const res = await authFetch('/api/opportunities');

        if (!res.ok) {
          throw new Error('Failed to load opportunities');
        }

        const data = await res.json();

        if (Array.isArray(data)) {
          setOpps(data);
        } else {
          console.error('Unexpected opportunities response:', data);
          setOpps([]);
        }
      } catch (err) {
        console.error('Error fetching opportunities:', err);
        setOpps([]);
      } finally {
        setLoading(false);
      }
    };

    loadOpportunities();
  }, []);

  const getDateOnlyValue = (value) => {
    if (!value) return '';

    const dateString = String(value).split('T')[0];
    const [year, month, day] = dateString.split('-');

    if (!year || !month || !day) return '';

    return `${year}-${month}-${day}`;
  };

  const formatDateOnly = (value) => {
    const dateString = getDateOnlyValue(value);

    if (!dateString) return '—';

    const [year, month, day] = dateString.split('-');

    return `${month}/${day}/${year}`;
  };

  const getLocalDateFromDateOnly = (value) => {
    const dateString = getDateOnlyValue(value);

    if (!dateString) return null;

    const [year, month, day] = dateString.split('-').map(Number);

    if (!year || !month || !day) return null;

    return new Date(year, month - 1, day);
  };

  const getName = (opp) =>
    opp?.name ||
    [opp?.first_name, opp?.last_name].filter(Boolean).join(' ') ||
    opp?.client_name ||
    opp?.lead_name ||
    '—';

  const getType = (opp) =>
    opp?.type ||
    opp?.entity_type ||
    '—';

  const getStatus = (opp) =>
    opp?.status ||
    opp?.opportunity_status ||
    opp?.status_label ||
    'Unknown';

  const getCoverageType = (opp) =>
    opp?.coverageTypeName ||
    opp?.coverage_type_name ||
    opp?.coverage_type ||
    '—';

  const getCarrier = (opp) =>
    opp?.carrierName ||
    opp?.carrier_name ||
    '—';

  const getMedicareSupplementPlanType = (opp) =>
    opp?.coverageTypePlanTypeName ||
    opp?.coverage_type_plan_type_name ||
    opp?.coverage_type_plan_type ||
    opp?.planTypeName ||
    opp?.plan_type_name ||
    opp?.planType ||
    opp?.plan_type ||
    opp?.medicare_supplement_plan_type ||
    '';

  const getProduct = (opp) => {
    const coverageType = getCoverageType(opp);

    if (String(coverageType).trim().toLowerCase() === 'medicare supplement') {
      return (
        getMedicareSupplementPlanType(opp) ||
        opp?.productName ||
        opp?.product_name ||
        opp?.plan_name ||
        '—'
      );
    }

    return (
      opp?.productName ||
      opp?.product_name ||
      opp?.plan_name ||
      '—'
    );
  };

  const getNextStep = (opp) =>
    opp?.nextStep ||
    opp?.next_step ||
    '—';

  const getDueDateRaw = (opp) => opp?.dueDate || opp?.due_date || '';

  const getDueDate = (opp) => {
    return formatDateOnly(getDueDateRaw(opp));
  };

  const getEntityId = (opp) =>
    opp?.entityId ||
    opp?.entity_id ||
    opp?.client_id ||
    opp?.lead_id;

  const statusClass = (value) => {
    const normalized = String(value || '')
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/\//g, '_')
      .replace(/-+/g, '_');

    return `sales-chip ${normalized}`;
  };

  const getDueState = (opp) => {
    const raw = getDueDateRaw(opp);
    if (!raw) return 'none';

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const due = getLocalDateFromDateOnly(raw);
    if (!due) return 'none';
    due.setHours(0, 0, 0, 0);

    const diffDays = Math.round((due - today) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'overdue';
    if (diffDays === 0) return 'today';
    if (diffDays <= 3) return 'soon';
    return 'normal';
  };

  const filteredOpps = useMemo(() => {
    return opps
      .filter((o) => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();

        return [
          getName(o),
          getType(o),
          getStatus(o),
          getCoverageType(o),
          getCarrier(o),
          getProduct(o),
          getNextStep(o),
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));
      })
      .filter((o) => !filters.type || getType(o) === filters.type)
      .filter((o) => !filters.coverageType || getCoverageType(o) === filters.coverageType)
      .filter((o) => !filters.carrier || getCarrier(o) === filters.carrier)
      .filter((o) => !filters.nextStep || getNextStep(o) === filters.nextStep);
  }, [opps, searchTerm, filters]);

  const sortedOpps = useMemo(() => {
    const sorted = [...filteredOpps].sort((a, b) => {
      const getSortValue = (opp) => {
        switch (sortField) {
          case 'name':
            return getName(opp).toLowerCase();
          case 'type':
            return getType(opp).toLowerCase();
          case 'status':
            return getStatus(opp).toLowerCase();
          case 'coverageType':
            return getCoverageType(opp).toLowerCase();
          case 'carrier':
            return getCarrier(opp).toLowerCase();
          case 'product':
            return getProduct(opp).toLowerCase();
          case 'nextStep':
            return getNextStep(opp).toLowerCase();
          case 'dueDate': {
            const raw = getDueDateRaw(opp);

            if (!raw) {
              return sortDirection === 'asc' ? Number.MAX_SAFE_INTEGER : 0;
            }

            const due = getLocalDateFromDateOnly(raw);

            return due
              ? due.getTime()
              : sortDirection === 'asc'
                ? Number.MAX_SAFE_INTEGER
                : 0;
          }
          default:
            return '';
        }
      };

      const aVal = getSortValue(a);
      const bVal = getSortValue(b);

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredOpps, sortField, sortDirection]);

  const uniqueTypes = Array.from(new Set(opps.map((o) => getType(o)).filter(Boolean)));
  const uniqueCoverageTypes = Array.from(
    new Set(opps.map((o) => getCoverageType(o)).filter(Boolean))
  );
  const uniqueCarriers = Array.from(
    new Set(opps.map((o) => getCarrier(o)).filter(Boolean))
  );
  const uniqueNextSteps = Array.from(
    new Set(opps.map((o) => getNextStep(o)).filter((value) => value && value !== '—'))
  );
  const uniqueStatuses = Array.from(
    new Set(opps.map((o) => getStatus(o)).filter(Boolean))
  );

  const handleRowClick = (opp) => {
    const entityId = getEntityId(opp);
    const basePath = getType(opp) === 'Client' ? '/client-profile/' : '/lead-profile/';

    if (!entityId) {
      console.error('Opportunity missing entity id:', opp);
      return;
    }

    navigate(`${basePath}${entityId}?opportunityId=${opp.id}`);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilters({
      type: '',
      coverageType: '',
      carrier: '',
      nextStep: '',
    });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const toggleRowSelection = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allVisibleSelected =
    sortedOpps.length > 0 && sortedOpps.every((opp) => selectedIds.has(opp.id));

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (allVisibleSelected) {
        sortedOpps.forEach((opp) => next.delete(opp.id));
      } else {
        sortedOpps.forEach((opp) => next.add(opp.id));
      }

      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const handleBulkUpdate = async () => {
    const updates = {};

    if (bulkStatus) updates.status = bulkStatus;
    if (bulkNextStep.trim()) updates.nextStep = bulkNextStep.trim();
    if (bulkDueDate) updates.dueDate = bulkDueDate;

    if (Object.keys(updates).length === 0) {
      alert('Choose at least one field to update.');
      return;
    }

    try {
      setBulkSaving(true);

      const data = await authJsonFetch('/api/opportunities/bulk-update', {
        method: 'PATCH',
        body: JSON.stringify({
          ids: Array.from(selectedIds),
          updates,
        }),
      });

      if (!data) {
        throw new Error('Bulk update failed.');
      }

      setOpps((prev) =>
        prev.map((opp) =>
          selectedIds.has(opp.id)
            ? {
                ...opp,
                ...(bulkStatus ? { status: bulkStatus } : {}),
                ...(bulkNextStep.trim() ? { nextStep: bulkNextStep.trim() } : {}),
                ...(bulkDueDate ? { dueDate: bulkDueDate, due_date: bulkDueDate } : {}),
              }
            : opp
        )
      );

      setSelectedIds(new Set());
      setBulkStatus('');
      setBulkNextStep('');
      setBulkDueDate('');
    } catch (error) {
      console.error('Bulk update error:', error);
      alert(error.message || 'Bulk update failed.');
    } finally {
      setBulkSaving(false);
    }
  };

  const getExportRows = () =>
    sortedOpps.map((o) => ({
      Name: getName(o),
      Type: getType(o),
      Status: getStatus(o),
      'Coverage Type': getCoverageType(o),
      Carrier: getCarrier(o),
      Product: getProduct(o),
      'Next Step': getNextStep(o),
      'Due Date': getDueDate(o) === '—' ? '' : getDueDate(o),
    }));

  const downloadCsv = (filename, rows) => {
    const csv = Papa.unparse(rows);
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportCSV = () => {
    downloadCsv('opportunities.csv', getExportRows());
    setExportOpen(false);
  };

  const exportPDF = () => {
    const doc = new jsPDF();

    autoTable(doc, {
      head: [['Name', 'Type', 'Status', 'Coverage Type', 'Carrier', 'Product', 'Next Step', 'Due Date']],
      body: sortedOpps.map((o) => [
        getName(o),
        getType(o),
        getStatus(o),
        getCoverageType(o),
        getCarrier(o),
        getProduct(o),
        getNextStep(o),
        getDueDate(o) === '—' ? '' : getDueDate(o),
      ]),
      startY: 20,
      styles: { fontSize: 8 },
    });

    doc.save('opportunities.pdf');
    setExportOpen(false);
  };

  const renderSortLabel = (field, label) => {
    if (sortField !== field) return label;
    return `${label} ${sortDirection === 'asc' ? '↑' : '↓'}`;
  };

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page">
        <div className="sales-hero">
          <div>
            <p className="sales-eyebrow">Agent Book CRM</p>
            <h1 className="sales-title">Opportunities</h1>
            <p className="sales-subtitle">
              Review open opportunity pipeline across leads and clients in one searchable view.
            </p>
          </div>
        </div>

        <div className="sales-toolbar-card">
          <div
            style={{
              display: 'flex',
              gap: '10px',
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <select
              name="type"
              value={filters.type}
              onChange={handleFilterChange}
              className="sales-search-input"
              style={{ maxWidth: '180px' }}
            >
              <option value="">All Types</option>
              {uniqueTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>

            <select
              name="coverageType"
              value={filters.coverageType}
              onChange={handleFilterChange}
              className="sales-search-input"
              style={{ maxWidth: '240px' }}
            >
              <option value="">All Coverage Types</option>
              {uniqueCoverageTypes.map((coverageType) => (
                <option key={coverageType} value={coverageType}>
                  {coverageType}
                </option>
              ))}
            </select>

            <select
              name="carrier"
              value={filters.carrier}
              onChange={handleFilterChange}
              className="sales-search-input"
              style={{ maxWidth: '220px' }}
            >
              <option value="">All Carriers</option>
              {uniqueCarriers.map((carrier) => (
                <option key={carrier} value={carrier}>
                  {carrier}
                </option>
              ))}
            </select>

            <select
              name="nextStep"
              value={filters.nextStep}
              onChange={handleFilterChange}
              className="sales-search-input"
              style={{ maxWidth: '220px' }}
            >
              <option value="">All Next Steps</option>
              {uniqueNextSteps.map((nextStep) => (
                <option key={nextStep} value={nextStep}>
                  {nextStep}
                </option>
              ))}
            </select>

            <input
              type="text"
              placeholder="Search name, status, carrier, product..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="sales-search-input"
              style={{ minWidth: '260px', flex: 1 }}
            />

            <button
              onClick={clearFilters}
              className="sales-secondary-btn"
              type="button"
            >
              Clear Filters
            </button>

            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setExportOpen(!exportOpen)}
                className="sales-secondary-btn"
                type="button"
              >
                Export Opps ▾
              </button>

              {exportOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    minWidth: '160px',
                    background: '#fff',
                    border: '1px solid rgba(226, 232, 240, 0.95)',
                    borderRadius: '14px',
                    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.12)',
                    overflow: 'hidden',
                    zIndex: 50,
                  }}
                >
                  <button
                    onClick={exportCSV}
                    type="button"
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '10px 12px',
                      border: 'none',
                      background: '#fff',
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    Export CSV
                  </button>
                  <button
                    onClick={exportPDF}
                    type="button"
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '10px 12px',
                      border: 'none',
                      background: '#fff',
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    Export PDF
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {selectedIds.size > 0 && (
          <div className="sales-toolbar-card">
            <div
              style={{
                display: 'flex',
                gap: '10px',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <strong>{selectedIds.size} selected</strong>

              <select
                value={bulkStatus}
                onChange={(e) => setBulkStatus(e.target.value)}
                className="sales-search-input"
                style={{ maxWidth: '220px' }}
              >
                <option value="">Set status...</option>
                {uniqueStatuses.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>

              <input
                type="text"
                value={bulkNextStep}
                onChange={(e) => setBulkNextStep(e.target.value)}
                placeholder="Set next step..."
                className="sales-search-input"
                style={{ maxWidth: '240px' }}
              />

              <input
                type="date"
                value={bulkDueDate}
                onChange={(e) => setBulkDueDate(e.target.value)}
                className="sales-search-input"
                style={{ maxWidth: '180px' }}
              />

              <button
                className="sales-primary-btn"
                onClick={handleBulkUpdate}
                disabled={bulkSaving}
                type="button"
              >
                {bulkSaving ? 'Saving...' : 'Apply Bulk Update'}
              </button>

              <button
                className="sales-secondary-btn"
                onClick={clearSelection}
                type="button"
              >
                Clear Selection
              </button>
            </div>
          </div>
        )}

        <div className="sales-table-card">
          <div className="sales-table-header">
            <div>
              <h2>All Opportunities</h2>
              <p>
                {sortedOpps.length} opportunit{sortedOpps.length === 1 ? 'y' : 'ies'}
              </p>
            </div>
          </div>

          <div className="sales-table-wrap">
            <table className="sales-table">
              <thead>
                <tr>
                  <th onClick={(e) => e.stopPropagation()} style={{ width: '44px', cursor: 'default' }}>
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAllVisible}
                    />
                  </th>
                  <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                    {renderSortLabel('name', 'Name')}
                  </th>
                  <th onClick={() => handleSort('type')} style={{ cursor: 'pointer' }}>
                    {renderSortLabel('type', 'Type')}
                  </th>
                  <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                    {renderSortLabel('status', 'Status')}
                  </th>
                  <th onClick={() => handleSort('coverageType')} style={{ cursor: 'pointer' }}>
                    {renderSortLabel('coverageType', 'Coverage Type')}
                  </th>
                  <th onClick={() => handleSort('carrier')} style={{ cursor: 'pointer' }}>
                    {renderSortLabel('carrier', 'Carrier')}
                  </th>
                  <th onClick={() => handleSort('product')} style={{ cursor: 'pointer' }}>
                    {renderSortLabel('product', 'Product')}
                  </th>
                  <th onClick={() => handleSort('nextStep')} style={{ cursor: 'pointer' }}>
                    {renderSortLabel('nextStep', 'Next Step')}
                  </th>
                  <th onClick={() => handleSort('dueDate')} style={{ cursor: 'pointer' }}>
                    {renderSortLabel('dueDate', 'Due Date')}
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="sales-empty-cell">
                      Loading opportunities...
                    </td>
                  </tr>
                ) : sortedOpps.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="sales-empty-cell">
                      No opportunities found.
                    </td>
                  </tr>
                ) : (
                  sortedOpps.map((opp) => {
                    const dueState = getDueState(opp);

                    return (
                      <tr
                        key={opp.id}
                        onClick={() => handleRowClick(opp)}
                        className="sales-click-row"
                        style={{
                          cursor: 'pointer',
                          ...(dueState === 'overdue'
                            ? { background: 'rgba(254, 226, 226, 0.55)' }
                            : dueState === 'today'
                              ? { background: 'rgba(254, 243, 199, 0.55)' }
                              : dueState === 'soon'
                                ? { background: 'rgba(219, 234, 254, 0.45)' }
                                : {}),
                        }}
                      >
                        <td onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(opp.id)}
                            onChange={() => toggleRowSelection(opp.id)}
                          />
                        </td>

                        <td>
                          <div className="sales-primary-cell">
                            <div className="sales-name">{getName(opp)}</div>
                            <div className="sales-secondary">
                              Opportunity ID: {opp.id}
                            </div>
                          </div>
                        </td>

                        <td>{getType(opp)}</td>

                        <td>
                          <span className={statusClass(getStatus(opp))}>
                            {getStatus(opp)}
                          </span>
                        </td>

                        <td>{getCoverageType(opp)}</td>
                        <td>{getCarrier(opp)}</td>
                        <td>{getProduct(opp)}</td>
                        <td>{getNextStep(opp)}</td>

                        <td>
                          <div className="sales-primary-cell">
                            <div className="sales-name">{getDueDate(opp)}</div>
                            <div className="sales-secondary">
                              {dueState === 'overdue'
                                ? 'Overdue'
                                : dueState === 'today'
                                  ? 'Due today'
                                  : dueState === 'soon'
                                    ? 'Due soon'
                                    : ''}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

export default Opportunities;